import { useState, useMemo } from "react"
import { Chart, TableVisualization } from "~/ui-rtk/components/ui/charts"
import {
  Button,
  DateRangePicker,
  MultiSelect,
} from "~/ui-rtk/components/ui/controls"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { usePeriod, DATE_FORMAT, isValidDate } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import {
  VISUALIZATION_TYPE,
  VISUALIZATIONS_MAP,
} from "~/ui-rtk/constants/charts"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import useSources from "~/ui-rtk/hooks/sources"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import type { Filter } from "@cubejs-client/core"
import { TRIGGERED_PATTERNS, SOCIAL_REFERRAL_SOURCES } from "../constants"

const dayjs = useDate()

type ChannelTab = "total" | "baseline" | "triggered" | "paid"

const CHANNEL_TAB_OPTIONS: { id: ChannelTab; label: string }[] = [
  { id: "total", label: "Total" },
  { id: "baseline", label: "Baseline" },
  { id: "triggered", label: "Triggered" },
  { id: "paid", label: "Paid" },
]

function buildChannelFilter(
  channel: ChannelTab,
  cubeName: string,
): Filter[] | null {
  const src = `${cubeName}.session_source`
  const med = `${cubeName}.session_medium`

  switch (channel) {
    case "total":
      return null

    case "baseline":
      // (source = '(direct)' OR medium = 'organic')
      // OR (medium = 'referral' AND source contains social domains)
      return [
        {
          or: [
            { member: src, operator: "equals", values: ["(direct)"] },
            { member: med, operator: "equals", values: ["organic"] },
            {
              and: [
                { member: med, operator: "equals", values: ["referral"] },
                {
                  or: SOCIAL_REFERRAL_SOURCES.map(domain => ({
                    member: src,
                    operator: "contains" as const,
                    values: [domain],
                  })),
                },
              ],
            },
          ],
        },
      ]

    case "triggered":
      // medium contains triggered patterns OR source contains triggered patterns
      return [
        {
          or: [
            ...TRIGGERED_PATTERNS.map(p => ({
              member: med,
              operator: "contains" as const,
              values: [p],
            })),
            ...TRIGGERED_PATTERNS.map(p => ({
              member: src,
              operator: "contains" as const,
              values: [p],
            })),
          ],
        },
      ]

    case "paid":
      // NOT baseline AND NOT triggered
      return [
        { member: src, operator: "notEquals", values: ["(direct)"] },
        { member: med, operator: "notEquals", values: ["organic"] },
        // Exclude social referral (medium=referral + social domain source)
        {
          or: [
            { member: med, operator: "notEquals", values: ["referral"] },
            {
              and: SOCIAL_REFERRAL_SOURCES.map(domain => ({
                member: src,
                operator: "notContains" as const,
                values: [domain],
              })),
            },
          ],
        },
        // Exclude triggered patterns from both medium and source
        ...TRIGGERED_PATTERNS.map(p => ({
          member: med,
          operator: "notContains" as const,
          values: [p],
        })),
        ...TRIGGERED_PATTERNS.map(p => ({
          member: src,
          operator: "notContains" as const,
          values: [p],
        })),
      ]

    default:
      return null
  }
}

const TAB_CONFIG = [
  {
    label: "Landing Page",
    widgetId: "company_web_analytics_page_depth_first_pageview_table",
    urlPrefix: "page_depth_landing",
    nameDimension: "first_pageview_page_path",
    cubeName: "company_web_analytics_page_value_first_pageview",
    sortKey: "company_web_analytics_page_value_first_pageview.session_count",
  },
  {
    label: "2nd Page",
    widgetId: "company_web_analytics_page_depth_second_pageview_table",
    urlPrefix: "page_depth_2nd",
    nameDimension: "second_pageview_page_path",
    cubeName: "company_web_analytics_page_value_second_pageview",
    sortKey: "company_web_analytics_page_value_second_pageview.session_count",
  },
  {
    label: "3rd Page",
    widgetId: "company_web_analytics_page_depth_third_pageview_table",
    urlPrefix: "page_depth_3rd",
    nameDimension: "third_pageview_page_path",
    cubeName: "company_web_analytics_page_value_third_pageview",
    sortKey: "company_web_analytics_page_value_third_pageview.session_count",
  },
] as const

const TAB_OPTIONS = TAB_CONFIG.map(({ label }) => ({ label }))

export default function PageDepthAnalysisPage() {
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [activeChannel, setActiveChannel] = useState<ChannelTab>("total")
  const { dateRange, compareRange } = useMinMaxDateRange()
  const { granularity, setPeriod, setComparePeriod } = usePeriod()
  const { sources } = useSources()

  const activeTab = TAB_CONFIG[activeTabIndex]
  const tableProps = useMemo(
    () => VISUALIZATIONS_MAP[activeTab.widgetId]?.props,
    [activeTab.widgetId],
  )

  const cubeFilters = useMemo((): TCubeFilterOptions | undefined => {
    const filters = buildChannelFilter(activeChannel, activeTab.cubeName)
    if (!filters) return undefined
    return { and: filters }
  }, [activeChannel, activeTab.cubeName])

  const defaultColumnKeys = useMemo(
    () =>
      tableProps?.columnConfig
        .filter(
          (column: TableVisualizationColumn & { disabled?: boolean }) =>
            !column.disabled,
        )
        .map((column: TableVisualizationColumn) => column.key) || [],
    [tableProps],
  )

  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    activeTab.urlPrefix,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(() => {
    const options = tableProps?.columnConfig.map(
      (column: TableVisualizationColumn) =>
        ({
          value: column.key,
          label: column.header,
        }) as TMultiSelectOption,
    )
    return options || []
  }, [tableProps])

  const columnConfigFiltered = useMemo(
    () =>
      (tableProps?.columnConfig ?? [])
        .filter(({ key }: { key: string }) =>
          (columnKeys as string[])?.includes(key),
        )
        .map(
          ({
            disabled,
            ...col
          }: TableVisualizationColumn & { disabled?: boolean }) => col,
        ),
    [tableProps?.columnConfig, columnKeys],
  )

  const changePeriod = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  return (
    <div className="px-4">
      <div className="flex items-center justify-between">
        <Breadcrumbs
          items={[
            {
              name: "Web Merchandising",
              slug: brandRoutes.WEB_MERCHANDISING.ROOT,
            },
            {
              name: "Page Depth Analysis",
              slug: brandRoutes.WEB_MERCHANDISING.PAGE_DEPTH_ANALYSIS,
            },
          ]}
        />
      </div>

      <div className="my-system-lg flex items-center justify-between flex-wrap gap-4">
        <div className="join">
          {CHANNEL_TAB_OPTIONS.map(({ id, label }) => (
            <Button
              variant={{ variant: "solid", color: "blue" }}
              key={id}
              title={label}
              className={cn(
                "join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md",
                { "bg-system-blue-800": activeChannel !== id },
                { "bg-system-blue-600": activeChannel === id },
              )}
              onClick={() => setActiveChannel(id)}
            >
              <span
                className={cn("font-[Lato]", {
                  "text-base text-system-gray-300 font-normal":
                    activeChannel !== id,
                  "text-white font-semibold": activeChannel === id,
                })}
              >
                {label}
              </span>
            </Button>
          ))}
        </div>
      </div>

      <div className="mb-system-md">
        <TabsGroup
          options={TAB_OPTIONS}
          index={activeTabIndex}
          onChange={setActiveTabIndex}
        />
      </div>

      <div className="flex justify-end items-stretch gap-system-sm mb-system-lg">
        <MultiSelect
          label="Columns"
          options={columnOptions as TMultiSelectOption[]}
          value={columnOptions.filter((option: TMultiSelectOption) =>
            (columnKeys as string[]).includes(option.value),
          )}
          onChange={(options: TMultiSelectOption[]) =>
            setColumnKeys(options.map(({ value }) => value))
          }
        />
        <TableFiltersBar
          id={activeTab.urlPrefix}
          columnConfig={columnConfigFiltered}
        />
        {dateRange?.[0] &&
          dateRange?.[1] &&
          isValidDate(dateRange[0]) &&
          isValidDate(dateRange[1]) && (
            <DateRangePicker
              start={dateRange[0]}
              end={dateRange[1]}
              compareStart={
                compareRange?.[0] && isValidDate(compareRange[0])
                  ? compareRange[0]
                  : undefined
              }
              compareEnd={
                compareRange?.[1] && isValidDate(compareRange[1])
                  ? compareRange[1]
                  : undefined
              }
              comparisonEnabled={true}
              onChange={changePeriod}
            />
          )}
      </div>

      <div className="mt-3">
        <Chart
          {...tableProps}
          chartId={activeTab.widgetId}
          urlParamsPrefix={activeTab.urlPrefix}
          className=""
          widgetType={VISUALIZATION_TYPE.TableVisualization}
          component={TableVisualization}
          dateRange={dateRange}
          compareRange={compareRange ?? undefined}
          comparisonEnabled={true}
          compareUniqueKey={`${activeTab.nameDimension}`}
          granularity={granularity}
          sources={sources}
          hideAggSelector={true}
          columnConfig={columnConfigFiltered}
          enableCSVExport={true}
          csvExportFilename={activeTab.urlPrefix}
          cubeFilters={cubeFilters}
          defaultSorting={[
            {
              id: activeTab.sortKey,
              desc: true,
            },
          ]}
        />
      </div>
    </div>
  )
}
